import { useState } from "react";
import axios from "axios";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Phase 8M.1 — web client ID drives the native Google Identity flow
// (no emergent hop). Falls back to the legacy emergent passthrough if
// the env var isn't set yet, so first deploys with placeholder values
// don't break sign-in.
const GOOGLE_CLIENT_ID_WEB =
    process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID_WEB ||
    process.env.REACT_APP_GOOGLE_CLIENT_ID ||
    "";

// REMINDER: DO NOT HARDCODE THE URL OR ADD ANY FALLBACKS — THIS BREAKS THE AUTH.
function startGoogleAuth() {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href =
        "https://auth.emergentagent.com/?redirect=" +
        encodeURIComponent(redirectUrl);
}

// Whitelist of safe in-app paths the login page may bounce back to. Prevents
// open-redirect abuse via `?redirect=https://evil.example/...`.
const SAFE_REDIRECTS = new Set(["/", "/delete-account"]);
function safeRedirect(qs) {
    const target = new URLSearchParams(qs).get("redirect") || "";
    return SAFE_REDIRECTS.has(target) ? target : "/";
}

export default function LoginPage() {
    const { login, persistSession } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    // Phase 8M.1 — verify a Google ID token directly with our backend,
    // skipping the emergent passthrough entirely.
    async function onGoogleCredential(credentialResponse) {
        const idToken = credentialResponse?.credential || "";
        if (!idToken) {
            setError("Google sign-in failed");
            return;
        }
        setError("");
        setLoading(true);
        try {
            const res = await axios.post(`${BACKEND_URL}/api/auth/google_native`, {
                id_token: idToken,
                platform: "web",
            });
            if (persistSession) {
                await persistSession(res.data.access_token, res.data.user);
            }
            navigate(safeRedirect(location.search), { replace: true });
        } catch (err) {
            setError(err?.response?.data?.detail || "Google sign-in failed");
        } finally {
            setLoading(false);
        }
    }

    async function onSubmit(e) {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email.trim(), password);
            navigate(safeRedirect(location.search), { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail || "Login failed";
            setError(detail);
        } finally {
            setLoading(false);
        }
    }

    return (
        <AuthShell title="Welcome back" subtitle="Sign in to continue your trips">
            <form onSubmit={onSubmit} className="flex flex-col gap-3" data-testid="login-form">
                <Field
                    label={t("auth.email")}
                    type="email"
                    value={email}
                    onChange={setEmail}
                    testId="login-email"
                />
                <Field
                    label={t("auth.password")}
                    type="password"
                    value={password}
                    onChange={setPassword}
                    testId="login-password"
                />
                {error && (
                    <p className="text-[12px] text-[#DC2626]" data-testid="login-error">
                        {error}
                    </p>
                )}
                <button
                    type="submit"
                    disabled={loading || !email || !password}
                    data-testid="login-submit"
                    className="mt-2 inline-flex h-10 items-center justify-center rounded-lg bg-[#0A0A0A] text-[14px] font-medium text-white transition hover:bg-[#262626] disabled:bg-[#E5E5E5] disabled:text-[#A3A3A3]"
                >
                    {loading ? t("common.loading") : t("auth.sign_in_cta")}
                </button>
            </form>

            <Divider />

            {GOOGLE_CLIENT_ID_WEB ? (
                <div data-testid="login-google-native" className="flex justify-center">
                    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID_WEB}>
                        <GoogleLogin
                            onSuccess={onGoogleCredential}
                            onError={() => setError("Google sign-in failed")}
                            theme="outline"
                            size="large"
                            text="continue_with"
                            shape="rectangular"
                            useOneTap={false}
                        />
                    </GoogleOAuthProvider>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={startGoogleAuth}
                    data-testid="login-google"
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-[#E5E5E5] bg-white text-[14px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A] hover:bg-[#F5F5F5]"
                >
                    <GoogleIcon />
                    {t("auth.continue_with_google")}
                </button>
            )}

            <p className="mt-6 text-center text-[13px] text-[#737373]">
                New to Piqpath?{" "}
                <Link
                    to="/signup"
                    className="font-medium text-[#0A0A0A] underline underline-offset-2"
                    data-testid="link-to-signup"
                >
                    Create an account
                </Link>
            </p>
        </AuthShell>
    );
}

export function AuthShell({ title, subtitle, children }) {
    return (
        <div className="flex min-h-screen items-center justify-center bg-white px-5 py-10 text-[#0A0A0A]">
            <div className="w-full max-w-[380px]">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 transition hover:opacity-80"
                    data-testid="auth-logo-link"
                >
                    <Logo size="md" />
                </Link>
                <h1 className="mt-10 text-3xl font-semibold tracking-tight">{title}</h1>
                {subtitle && (
                    <p className="mt-2 text-[14px] text-[#737373]">{subtitle}</p>
                )}
                <div className="mt-8 rounded-2xl border border-[#E5E5E5] bg-white p-6">
                    {children}
                </div>
            </div>
        </div>
    );
}

export function Field({ label, type, value, onChange, testId }) {
    return (
        <label className="flex flex-col gap-1.5 text-[12px] font-medium text-[#737373]">
            {label}
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="h-10 rounded-lg border border-[#E5E5E5] bg-white px-3 text-[14px] text-[#0A0A0A] outline-none transition focus:border-[#0A0A0A]"
                autoComplete={type === "password" ? "current-password" : "email"}
            />
        </label>
    );
}

export function Divider() {
    return (
        <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-wider text-[#A3A3A3]">
            <span className="h-px flex-1 bg-[#E5E5E5]" />
            or
            <span className="h-px flex-1 bg-[#E5E5E5]" />
        </div>
    );
}

export function GoogleIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 48 48" aria-hidden="true">
            <path
                fill="#FFC107"
                d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.5-5.9 7.7-11.3 7.7-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
            />
            <path
                fill="#FF3D00"
                d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"
            />
            <path
                fill="#4CAF50"
                d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.4 0-9.7-3.2-11.3-7.7l-6.6 5C9.5 39.6 16.2 44 24 44z"
            />
            <path
                fill="#1976D2"
                d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.4 4.2-4.4 5.6l6.2 5.2C40 35.4 44 30.2 44 24c0-1.3-.1-2.4-.4-3.5z"
            />
        </svg>
    );
}
